/**
 * Merge a fresh getProject() snapshot into the current local packets,
 * preserving local block content.
 *
 * During a streaming create two writers touch the same packets: the teacher
 * (edits sync up from ProjectPacketContext ahead of a debounced save) and the
 * generation pipeline (activities landing server-side, read back via snapshots
 * that may predate that save). Replacing local state with the snapshot — as
 * every activity_ready refetch, the done reconcile, and post-done retries used
 * to do — silently reverts the teacher's unsaved edits.
 *
 * The generator is APPEND-ONLY: it never modifies a block after it lands
 * (deferred images resolve through the separate image_id overlay, applied on
 * top of this merge). So the correct merge is:
 *
 * - block in both:       LOCAL wins (may carry unsaved edits newer than the snapshot)
 * - block only in fresh: take it (a newly-landed activity)
 * - block only local:    keep it, after the snapshot's blocks (an unsaved local add)
 * - loading placeholders (canonical_id 'loading') are display-only, never merged
 *
 * Known trade-off: a block deleted locally but not yet saved re-appears until
 * the save lands — without tombstones a local deletion is indistinguishable
 * from a server-side add. The window is the save debounce (~1s).
 */
export const isLoadingPlaceholderBlock = (block) => block?.canonical_id === 'loading';

/**
 * True when a resource is not in a state that can be exported: it is still
 * generating, or it has nothing to export.
 *
 * `_isLoadingSecondary` — a secondary (or a synthetic placeholder tab) before
 * its packet_ready lands.
 * A `loading*` block — the display placeholder, also used by chat's "add
 * activity" (usePacketState.addBlock). `startsWith` rather than
 * isLoadingPlaceholderBlock's `=== 'loading'`, to match what the render paths
 * test (project.jsx, HeaderTabs.jsx): the question here is "would the teacher
 * see a Loading… block", which is their question too.
 * Zero blocks — an unfinished secondary row committed by unlimited.py at
 * packet_started, after a reload that lost the stream session. The screenshot
 * service already refuses this (`blocks > 0`), so nothing exportable is lost.
 *
 * Callers must ALSO consult ProjectContext's isPacketGenerating: these are
 * packet-shape signals and an edit can strip the block marker mid-generation.
 */
export const isPacketNotExportable = (packet) =>
  !!packet?._isLoadingSecondary ||
  !(packet?.blocks?.length > 0) ||
  packet.blocks.some((b) => b?.canonical_id?.startsWith('loading'));

export function mergeBlocksPreservingLocal(localBlocks, freshBlocks) {
  const localReal = (localBlocks || []).filter((b) => !isLoadingPlaceholderBlock(b));
  const fresh = (freshBlocks || []).filter((b) => !isLoadingPlaceholderBlock(b));
  if (!localReal.length) return fresh;

  const localIds = new Set(localReal.map((b) => b.unique_id));
  // Nothing new in the snapshot → local wins outright (content, order, adds).
  if (fresh.every((b) => localIds.has(b.unique_id))) return localReal;

  const localById = new Map(localReal.map((b) => [b.unique_id, b]));
  const freshIds = new Set(fresh.map((b) => b.unique_id));
  const merged = fresh.map((b) => localById.get(b.unique_id) ?? b);
  for (const b of localReal) {
    if (!freshIds.has(b.unique_id)) merged.push(b);
  }
  return merged;
}

/**
 * Packet-level merge: wrapper metadata (position, premium_blocks, …) comes from
 * the snapshot; packet fields (title, settings — also teacher-editable) come
 * from local; blocks are merged per the block rule above. A packet with no
 * local counterpart is taken from the snapshot as-is. Packets that exist only
 * locally (placeholder tabs) are NOT re-added — the caller re-injects those.
 *
 * Exception: `orientation` (16x9 slides vs 8.5x11 doc) is generation-controlled,
 * not teacher-editable, and is resolved server-side as secondaries finalize. It
 * must come from the fresh snapshot — preserving the local value here let a
 * mixed-orientation secondary keep the primary's format until a hard refresh.
 *
 * Same exception for a secondary's `title` while it is still generating. The row
 * is created at packet_started under a provisional name and renamed at
 * packet_ready (a translation's real name is the translated one, which isn't
 * known until the translation returns). A secondary in that window can't carry a
 * teacher edit — its tab is a non-clickable spinner until packet_ready — so the
 * snapshot wins. Once the spinner clears, the title is teacher-editable again
 * and local wins as usual.
 */
export function mergePacketsPreservingLocal(prevPackets, freshPackets) {
  const prevById = new Map(
    (prevPackets || []).filter((p) => p?.packet?.id).map((p) => [p.packet.id, p])
  );
  return (freshPackets || []).map((fp) => {
    const lp = prevById.get(fp?.packet?.id);
    if (!lp?.packet) return fp;
    return {
      ...fp,
      packet: {
        ...lp.packet,
        orientation: fp.packet.orientation ?? lp.packet.orientation,
        title: lp.packet._isLoadingSecondary
          ? (fp.packet.title ?? lp.packet.title)
          : lp.packet.title,
        blocks: mergeBlocksPreservingLocal(lp.packet.blocks, fp.packet.blocks),
      },
    };
  });
}
